import { _ } from "../../polyglot/polyglot.js";
import { checkIcon, pencilSolidIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Decorate a renderable content with an 'edit' button.
 * When clicked, the renderable is replaced with an input field.
 *
 * On each keyDown the `contentChanged` event is triggered with the new content.
 * On save, the `contentSaved` event is triggered with the new content and the input field
 * is replaced with the renderable.
 *
 * @param {Renderable} placeholder - Placeholder to be displayed and edited.
 * @param {function} getContent - Function that returns a string representing the content to be displayed and edited.
 */
export default class InlineEditorWidget extends Widget {
	constructor({ getContent = () => "", placeholder = "" }) {
		super(...arguments);
		this._getContent = getContent;
		this._placeholder = placeholder;

		this._showEditor = false;

		this.contentChanged = this._events.createEvent();
		this.contentSaved = this._events.createEvent();
	}

	renderContentOn(html) {
		html.span(
			{
				klass: "inline-editor",
			},
			this._showEditor ? this._editorRenderer() : this._viewerRenderer(),
		);
	}

	_viewerRenderer() {
		return (html) => {
			html.label(
				{ click: () => this._toggleEditor() },
				this._getContent() || this._placeholder,
			);

			html.render(this._editButtonRenderer());
		};
	}

	_editorRenderer() {
		return (html) => {
			html.div(
				{ class: "editor-content" },
				html.label(
					{ class: "hide-me" },
					this._getContent() || this._placeholder,
				),
				html.input({
					"data-test": "inline-editor",
					type: "text",
					name: "inline-editor",
					value: this._getContent() || "",
					klass: "form-control",
					keyup: () => this._saveContent(),
					keydown: (event) => this._handleInputKeyDown(event),
				}),
			);

			html.render(this._closeButtonRenderer());
		};
	}

	_didAttach() {
		this.asJQuery().find("input").focus();
	}

	_editButtonRenderer() {
		return (html) => {
			html.a(
				{
					class: "icon-container",
					title: _("Edit"),
					click: () => this._toggleEditor(),
				},
				pencilSolidIcon({ dataTest: "edit" }),
			);
		};
	}

	_closeButtonRenderer() {
		return (html) => {
			html.a(
				{
					class: "icon-container",
					title: _("Close"),
					click: this._submit.bind(this),
				},
				checkIcon({ dataTest: "close" }),
			);
		};
	}

	_submit() {
		this._toggleEditor();
		this.contentSaved.trigger();
	}

	_toggleEditor() {
		this._showEditor = !this._showEditor;
		this.update();
	}

	_saveContent() {
		const content = this.asJQuery().find("input").val();
		this.contentChanged.trigger(content);
	}

	/**
	 * Close the editor when the user presses enter.
	 */
	_handleInputKeyDown(event) {
		if (event.keyCode !== 13) {
			return;
		}
		this._submit();
	}
}
